import { useEffect, useState, useRef } from "react";
import api from "../api/api";
import {
  Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, Button, Box, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, IconButton, Tooltip, CircularProgress, Chip
} from "@mui/material";
import AddIcon          from "@mui/icons-material/Add";
import EditIcon         from "@mui/icons-material/Edit";
import DeleteIcon       from "@mui/icons-material/Delete";
import UploadFileIcon   from "@mui/icons-material/UploadFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useAuth } from "../components/AuthContext";

const baseURL = process.env.REACT_APP_API_URL || "";

const ROWS_OPTIONS = [10, 25, 50];

export default function Fichas() {
  const { hasPermission } = useAuth();
  const [data, setData]           = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit]   = useState(false);
  const [selected, setSelected]   = useState(null);
  const [form, setForm]           = useState({ codigo: "", nombre: "", version: "" });
  const [pagina, setPagina]       = useState(0);
  const [rowsPorPagina, setRowsPorPagina] = useState(10);
  const [uploadingFile, setUploadingFile] = useState(null);
  const fileInputRef = useRef();

  const cargar = async () => {
    try {
      const res = await api.get("/fichas/catalogo");
      setData(res.data);
      setPagina(0);
    } catch (error) {
      console.error("Error loading working sheets:", error);
    }
  };

  useEffect(() => { cargar(); }, []);

  const handleCreate = async () => {
    if (!form.codigo.trim() || !form.nombre.trim()) return alert("Enter a code and a name");
    try {
      await api.post("/fichas", { codigo: form.codigo, nombre: form.nombre, version: form.version || null });
      setOpenCreate(false);
      setForm({ codigo: "", nombre: "", version: "" });
      cargar();
    } catch (error) {
      alert("Error creating working sheet: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdate = async () => {
    if (!form.nombre.trim()) return alert("Enter a name");
    try {
      await api.put(`/fichas/${selected.codigo}`, { nombre: form.nombre, version: form.version || null });
      setOpenEdit(false);
      setSelected(null);
      setForm({ codigo: "", nombre: "", version: "" });
      cargar();
    } catch (error) {
      alert("Error updating working sheet: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (codigo) => {
    if (!window.confirm("Delete this working sheet?")) return;
    try {
      await api.delete(`/fichas/${codigo}`);
      cargar();
    } catch (error) {
      alert("Error deleting working sheet: " + (error.response?.data?.detail || error.message));
    }
  };

  const openEditDialog = (f) => {
    setSelected(f);
    setForm({ codigo: f.codigo, nombre: f.nombre || "", version: f.version || "" });
    setOpenEdit(true);
  };

  const handleFileUpload = async (e, ficha) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    const formData = new FormData();
    formData.append("file", file);
    setUploadingFile(ficha.codigo);
    try {
      await api.post(`/fichas/${ficha.codigo}/archivo`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      cargar();
    } catch (err) {
      alert("Error uploading file: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploadingFile(null);
    }
  };

  const paginados = data.slice(pagina * rowsPorPagina, (pagina + 1) * rowsPorPagina);

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h4">Working Sheets</Typography>
        {hasPermission("FICHAS_CREAR") && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm({ codigo: "", nombre: "", version: "" }); setOpenCreate(true); }}>
            Create Working Sheet
          </Button>
        )}
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow sx={{ background: "#000" }}>
            <TableCell sx={{ color: "white" }}>Code</TableCell>
            <TableCell sx={{ color: "white" }}>Name</TableCell>
            <TableCell sx={{ color: "white" }}>Version</TableCell>
            <TableCell sx={{ color: "white" }}>File</TableCell>
            <TableCell sx={{ color: "white" }} align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginados.map(f => (
            <TableRow key={f.codigo} hover>
              <TableCell>{f.codigo}</TableCell>
              <TableCell>{f.nombre}</TableCell>
              <TableCell>{f.version || "—"}</TableCell>
              <TableCell>
                {f.archivo_url
                  ? (
                    <Chip
                      icon={<PictureAsPdfIcon />}
                      label="View PDF"
                      size="small"
                      color="error"
                      variant="outlined"
                      clickable
                      component="a"
                      href={`${baseURL}/${f.archivo_url.replace(/^fichas\//, "fichas_archivos/")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  )
                  : <Typography variant="body2" color="text.secondary">No file</Typography>
                }
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                  {hasPermission("FICHAS_EDITAR") && (
                    <Tooltip title={f.archivo_url ? "Replace PDF" : "Upload PDF"}>
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={uploadingFile === f.codigo}
                          onClick={() => { fileInputRef.current._ficha = f; fileInputRef.current.click(); }}
                        >
                          {uploadingFile === f.codigo
                            ? <CircularProgress size={16} />
                            : <UploadFileIcon fontSize="small" />
                          }
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {hasPermission("FICHAS_EDITAR") && (
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEditDialog(f)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {hasPermission("FICHAS_ELIMINAR") && (
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(f.codigo)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
          {data.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ color: "text.secondary", py: 4 }}>
                No working sheets registered
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {data.length > 0 && (
        <TablePagination
          component="div"
          count={data.length}
          page={pagina}
          onPageChange={(_, p) => setPagina(p)}
          rowsPerPage={rowsPorPagina}
          onRowsPerPageChange={e => { setRowsPorPagina(parseInt(e.target.value)); setPagina(0); }}
          rowsPerPageOptions={ROWS_OPTIONS}
          labelRowsPerPage="Rows:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
          sx={{ borderTop: "1px solid", borderColor: "divider" }}
        />
      )}

      <input
        type="file"
        accept="application/pdf"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={e => handleFileUpload(e, fileInputRef.current._ficha)}
      />

      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Working Sheet</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Code" value={form.codigo}
            onChange={e => setForm({ ...form, codigo: e.target.value })} sx={{ mt: 2 }} />
          <TextField fullWidth label="Name" value={form.nombre}
            onChange={e => setForm({ ...form, nombre: e.target.value })} sx={{ mt: 2 }} />
          <TextField fullWidth label="Version" value={form.version}
            onChange={e => setForm({ ...form, version: e.target.value })} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Working Sheet</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Code" value={form.codigo} disabled sx={{ mt: 2 }} />
          <TextField fullWidth label="Name" value={form.nombre}
            onChange={e => setForm({ ...form, nombre: e.target.value })} sx={{ mt: 2 }} />
          <TextField fullWidth label="Version" value={form.version}
            onChange={e => setForm({ ...form, version: e.target.value })} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>Update</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
